import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api, formatApiErrorDetail } from '../lib/api';
import { Btn, Card, Input, Label, PageHeader, Select } from '../components/Ui';
import { StatusBadge } from '../components/Badges';
import { Send, Download, FileText, Mail, Loader2, RefreshCw } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Accountant() {
  const [companies, setCompanies] = useState([]);
  const [companyCode, setCompanyCode] = useState('');
  const [month, setMonth] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [batches, setBatches] = useState([]);

  const [sentTo, setSentTo] = useState('');
  const [notes, setNotes] = useState('');
  const [period, setPeriod] = useState('');
  const [buildZip, setBuildZip] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const historyRef = useRef(null);
  const [highlightBatchId, setHighlightBatchId] = useState(null);

  const loadPending = useCallback(async () => {
    const params = {};
    if (companyCode) params.company_code = companyCode;
    if (month) {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return;
      params.month = month;
    }
    const { data } = await api.get('/api/accountant/pending', { params });
    setItems(data.items);
    setSelected(new Set());
  }, [companyCode, month]);

  const loadBatches = useCallback(async () => {
    const params = {};
    if (companyCode) params.company_code = companyCode;
    const { data } = await api.get('/api/accountant/batches', { params });
    setBatches(data);
  }, [companyCode]);

  useEffect(() => {
    api.get('/api/companies').then((r) => setCompanies(r.data));
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const createBatch = async () => {
    if (!companyCode) {
      toast.error('Selecciona una empresa');
      return;
    }
    if (!sentTo) {
      toast.error('Introduce el email de la asesoría');
      return;
    }
    if (selected.size === 0) {
      toast.error('Selecciona al menos un documento');
      return;
    }
    setSubmitting(true);
    const loadingId = toast.loading('Creando envío…');
    try {
      const { data } = await api.post('/api/accountant/batches', {
        company_code: companyCode,
        document_ids: Array.from(selected),
        sent_to: sentTo,
        notes: notes || null,
        period: period || null,
        build_zip: buildZip,
        send_email: sendEmail,
        email_subject: emailSubject || null,
      });
      toast.dismiss(loadingId);

      // Main success
      toast.success(`Envío #${data.id} creado`, {
        description: `${data.document_count} documento${data.document_count === 1 ? '' : 's'} enviado${data.document_count === 1 ? '' : 's'} a la asesoría`,
        duration: 6000,
      });

      // ZIP warning
      if (data.zip_warning) {
        toast.warning('ZIP no generado', {
          description: data.zip_warning,
          duration: 8000,
        });
      }

      // Email feedback
      if (sendEmail) {
        if (data.email_sent) {
          toast.success('Email enviado', {
            description: `A ${data.sent_to}${data.email_message_id ? ` (id: ${data.email_message_id.slice(0, 8)}…)` : ''}`,
            duration: 6000,
          });
        } else if (data.email_error) {
          toast.error('Email no enviado', {
            description: data.email_error,
            duration: 10000,
          });
        }
      }

      // Reset form + reload
      setSentTo('');
      setNotes('');
      setPeriod('');
      setBuildZip(false);
      setSendEmail(false);
      setEmailSubject('');
      setHighlightBatchId(data.id);
      await Promise.all([loadPending(), loadBatches()]);

      // Scroll to history
      setTimeout(() => {
        historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      setTimeout(() => setHighlightBatchId(null), 4000);
    } catch (e) {
      toast.dismiss(loadingId);
      toast.error('Error al crear envío', {
        description: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
        duration: 10000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCsv = async (batchId) => {
    const t = toast.loading('Generando CSV…');
    try {
      const token = localStorage.getItem('adm_token');
      const res = await fetch(`${API_BASE}/api/accountant/batches/${batchId}/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `batch_${batchId}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.dismiss(t);
      toast.success('CSV descargado', { description: filename });
    } catch (e) {
      toast.dismiss(t);
      toast.error('Error al descargar CSV', { description: e.message });
    }
  };

  const resendEmail = async (batchId) => {
    if (!window.confirm(`¿Reenviar email del envío #${batchId}?`)) return;
    setResendingId(batchId);
    const t = toast.loading(`Reenviando email del envío #${batchId}…`);
    try {
      const { data } = await api.post(`/api/accountant/batches/${batchId}/resend-email`);
      toast.dismiss(t);
      if (data.email_sent) {
        toast.success('Email reenviado', { description: `A ${data.sent_to}` });
      } else {
        toast.error('Email no enviado', { description: data.email_error || 'Error desconocido' });
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error('Error al reenviar', {
        description: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      });
    } finally {
      setResendingId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Envío a Asesoría"
        subtitle={`${items.length} documento${items.length === 1 ? '' : 's'} listo${items.length === 1 ? '' : 's'} para enviar`}
        testid="accountant-header"
      />

      <div className="p-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Pending list */}
        <Card testid="pending-card" className="xl:col-span-2">
          <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide2">Documentos por enviar</h2>
            <div className="flex items-end gap-3">
              <div>
                <Label>Empresa</Label>
                <Select
                  data-testid="acc-company"
                  value={companyCode}
                  onChange={(e) => setCompanyCode(e.target.value)}
                  className="w-40"
                >
                  <option value="">Todas</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.code}>{c.code}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Mes (YYYY-MM)</Label>
                <Input
                  data-testid="acc-month"
                  placeholder="2026-05"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-32"
                />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface text-muted">
                <tr className="text-[10px] uppercase tracking-wide2">
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      data-testid="select-all"
                      type="checkbox"
                      checked={items.length > 0 && selected.size === items.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-bold">Recibido</th>
                  <th className="px-3 py-2 text-left font-bold">Fecha factura</th>
                  <th className="px-3 py-2 text-left font-bold">Emp.</th>
                  <th className="px-3 py-2 text-left font-bold">Proveedor</th>
                  <th className="px-3 py-2 text-left font-bold">N°</th>
                  <th className="px-3 py-2 text-left font-bold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted">Sin documentos pendientes</td></tr>
                )}
                {items.map((d) => (
                  <tr
                    key={d.id}
                    data-testid={`pending-row-${d.id}`}
                    className={`border-t border-border ${selected.has(d.id) ? 'bg-accent/10' : 'hover:bg-surface'}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        data-testid={`select-doc-${d.id}`}
                        checked={selected.has(d.id)}
                        onChange={() => toggle(d.id)}
                      />
                    </td>
                    <td className="px-3 py-2 num whitespace-nowrap">{d.received_at ? new Date(d.received_at).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2 num text-muted whitespace-nowrap">{d.document_date || '—'}</td>
                    <td className="px-3 py-2 font-mono">{d.company_code}</td>
                    <td className="px-3 py-2">{d.supplier || '—'}</td>
                    <td className="px-3 py-2 font-mono">{d.document_number || '—'}</td>
                    <td className="px-3 py-2"><StatusBadge status={d.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Create batch */}
        <Card testid="create-batch-card">
          <div className="border-b border-ink/90 px-4 py-3">
            <h2 className="text-sm font-bold uppercase tracking-wide2">Crear envío</h2>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <Label>Empresa *</Label>
              <Select
                data-testid="batch-company"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
              >
                <option value="">— Seleccionar —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Email asesoría *</Label>
              <Input
                data-testid="batch-sentto"
                type="email"
                value={sentTo}
                onChange={(e) => setSentTo(e.target.value)}
                placeholder="asesoria@example.com"
              />
            </div>
            <div>
              <Label>Periodo (YYYY-MM)</Label>
              <Input
                data-testid="batch-period"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="2026-05"
              />
            </div>
            <div>
              <Label>Notas</Label>
              <textarea
                data-testid="batch-notes"
                className="w-full h-20 px-3 py-2 bg-white border border-ink text-sm focus:outline-none focus:border-accent"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="border-t border-border pt-3 space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  data-testid="batch-zip"
                  type="checkbox"
                  checked={buildZip}
                  onChange={(e) => setBuildZip(e.target.checked)}
                />
                <span>Generar <strong>ZIP</strong> en Wasabi</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  data-testid="batch-sendmail"
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                <span>Enviar <strong>email</strong> a la asesoría (Resend)</span>
              </label>
              {sendEmail && (
                <div className="pl-6">
                  <Label>Asunto del email (opcional)</Label>
                  <Input
                    data-testid="batch-email-subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Facturas XYZ — 2026-05"
                  />
                </div>
              )}
            </div>

            <div className="text-xs">
              <span className="text-muted">Seleccionados: </span>
              <span className="num font-bold" data-testid="selected-count">{selected.size}</span>
            </div>

            <Btn
              data-testid="batch-submit"
              onClick={createBatch}
              disabled={submitting}
              className="w-full"
              size="lg"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Enviando…
                </>
              ) : (
                <>
                  <Send size={14} /> Crear envío
                </>
              )}
            </Btn>
          </div>
        </Card>

        {/* Batch history */}
        <Card testid="batch-history" className="xl:col-span-3">
          <div
            ref={historyRef}
            className="border-b border-ink/90 px-4 py-3 flex items-center justify-between"
          >
            <h2 className="text-sm font-bold uppercase tracking-wide2">Histórico de envíos</h2>
            <span className="text-[10px] uppercase tracking-wide2 text-muted">
              {batches.length} envío{batches.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface text-muted">
                <tr className="text-[10px] uppercase tracking-wide2">
                  <th className="px-3 py-2 text-left font-bold">ID</th>
                  <th className="px-3 py-2 text-left font-bold">Emp.</th>
                  <th className="px-3 py-2 text-left font-bold">Periodo</th>
                  <th className="px-3 py-2 text-left font-bold">Enviado a</th>
                  <th className="px-3 py-2 text-right font-bold">N° doc.</th>
                  <th className="px-3 py-2 text-left font-bold">Fecha envío</th>
                  <th className="px-3 py-2 text-left font-bold">Notas</th>
                  <th className="px-3 py-2 text-right font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {batches.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-muted">Aún no hay envíos</td></tr>
                )}
                {batches.map((b) => {
                  const isHl = highlightBatchId === b.id;
                  return (
                    <tr
                      key={b.id}
                      data-testid={`batch-row-${b.id}`}
                      className={`border-t border-border ${isHl ? 'bg-accent/15 ring-2 ring-accent' : 'hover:bg-surface'}`}
                    >
                      <td className="px-3 py-2 num font-bold">#{b.id}</td>
                      <td className="px-3 py-2 font-mono">{b.company_code}</td>
                      <td className="px-3 py-2 font-mono">{b.period || '—'}</td>
                      <td className="px-3 py-2">{b.sent_to}</td>
                      <td className="px-3 py-2 num text-right">{b.document_count}</td>
                      <td className="px-3 py-2 num">{new Date(b.sent_at).toLocaleString()}</td>
                      <td className="px-3 py-2 max-w-xs truncate">{b.notes || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-3">
                          {b.zip_download_url ? (
                            <a
                              data-testid={`batch-zip-${b.id}`}
                              href={b.zip_download_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-accent hover:text-accentHover font-bold uppercase tracking-wide2"
                              title="Descargar ZIP desde Wasabi"
                            >
                              <Download size={12} /> zip
                            </a>
                          ) : null}
                          <button
                            data-testid={`batch-csv-${b.id}`}
                            onClick={() => downloadCsv(b.id)}
                            className="inline-flex items-center gap-1 text-ink hover:text-accent font-bold uppercase tracking-wide2"
                            title="Descargar CSV"
                          >
                            <FileText size={12} /> csv
                          </button>
                          <button
                            data-testid={`batch-resend-${b.id}`}
                            onClick={() => resendEmail(b.id)}
                            disabled={resendingId === b.id}
                            className="inline-flex items-center gap-1 text-ink hover:text-accent font-bold uppercase tracking-wide2 disabled:opacity-50"
                            title="Reenviar email"
                          >
                            {resendingId === b.id ? (
                              <RefreshCw size={12} className="animate-spin" />
                            ) : (
                              <Mail size={12} />
                            )}
                            mail
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
